"use client";

import { useEffect, useRef, useState } from "react";
import type { CharacterPersona, CharacterReaction, CharacterState } from "./appearance";
import { resolveIllustration } from "./illustratedCast";
import ProspectPortrait from "./ProspectPortrait";
import styles from "./ProspectCharacter.module.css";

interface Props {
  persona: CharacterPersona;
  state?: CharacterState;
  reaction?: CharacterReaction;
  className?: string;
  controls?: boolean;
  speakingSlot?: "primary" | "partner";
  blendIntoSurface?: boolean;
  readSpeechLevel?: () => number;
}

export default function ProspectCharacter(props: Props) {
  return <IllustratedProspect key={`${props.persona.name}:${props.persona.age ?? ""}:${props.persona.characterId ?? ""}`} {...props} />;
}

function IllustratedProspect({ persona, state = "idle", reaction = "neutral", className = "", controls = false, speakingSlot = "primary", blendIntoSurface = false, readSpeechLevel }: Props) {
  const member = resolveIllustration(persona);
  const host = useRef<HTMLDivElement>(null);
  const current = useRef({ state, reaction, motion: false, speakingSlot, readSpeechLevel });
  const redraw = useRef<(() => void) | null>(null);
  const [motion, setMotion] = useState(false);
  const [status, setStatus] = useState<"loading" | "ready" | "fallback">("loading");

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const change = () => setMotion(!media.matches);
    change();
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);

  useEffect(() => {
    current.current = { state, reaction, motion, speakingSlot, readSpeechLevel };
    redraw.current?.();
  }, [state, reaction, motion, speakingSlot, readSpeechLevel]);

  useEffect(() => {
    if (!member || !host.current) return;
    if (!motion) { setStatus("fallback"); return; }
    const element = host.current;
    let closed = false, inView = false, failures = 0;
    let abort: AbortController | undefined;
    let dispose: (() => void) | undefined;
    let retryTimer: number | undefined, loadTimer: number | undefined, stableTimer: number | undefined;
    let suspendTimer: number | undefined;
    const stop = () => {
      abort?.abort(); abort = undefined;
      dispose?.(); dispose = undefined;
      redraw.current = null;
      window.clearTimeout(loadTimer); window.clearTimeout(stableTimer);
    };
    const start = () => {
      window.clearTimeout(retryTimer);
      if (closed || abort || !inView || document.hidden || navigator.onLine === false) return;
      const controller = new AbortController();
      abort = controller;
      const failed = () => {
        if (closed || controller.signal.aborted) return;
        stop();
        setStatus("fallback");
        // Keep trying, but give a busy GPU or unavailable asset time to recover.
        retryTimer = window.setTimeout(start, Math.min(30_000, 1000 * 2 ** failures));
        failures = Math.min(failures + 1, 5);
      };
      setStatus("loading");
      loadTimer = window.setTimeout(failed, 15_000);
      void import("./illustratedRig").then(async ({ mountIllustration }) => {
        if (controller.signal.aborted) return;
        const rig = await mountIllustration(element, member, current, controller.signal, failed);
        if (controller.signal.aborted) { rig.dispose(); return; }
        window.clearTimeout(loadTimer);
        dispose = rig.dispose;
        redraw.current = rig.draw;
        setStatus("ready");
        rig.draw();
        stableTimer = window.setTimeout(() => { failures = 0; }, 30_000);
      }).catch(failed);
    };
    const resume = () => {
      if (document.hidden || !inView) {
        window.clearTimeout(retryTimer);
        if (abort && suspendTimer === undefined) {
          // Avoid reallocating for a quick scroll, then release hidden graphics.
          suspendTimer = window.setTimeout(() => { stop(); setStatus("fallback"); suspendTimer = undefined; }, 5000);
        }
        return;
      }
      window.clearTimeout(suspendTimer); suspendTimer = undefined;
      if (navigator.onLine !== false && !abort) start();
    };
    const intersection = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      resume();
    });
    intersection.observe(element);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);
    start();
    return () => {
      closed = true;
      window.clearTimeout(retryTimer);
      window.clearTimeout(suspendTimer);
      stop();
      intersection.disconnect();
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
    };
  }, [member, motion]);

  const animated = member && motion && status === "ready";
  return <div className={`relative isolate overflow-hidden rounded-xl ${className}`}>
    <div className={`absolute inset-x-0 top-0 ${controls ? "bottom-12" : "bottom-0"} ${blendIntoSurface ? styles.blendContainer : ""}`}>
      <div className={blendIntoSurface ? styles.blendedArt : "absolute inset-0"}>
        <div aria-hidden={animated ? true : undefined} className="absolute inset-0">
          <ProspectPortrait persona={persona} framing="figure" className="h-full w-full" />
        </div>
        {member && <div ref={host} role="img" aria-label={`${persona.name}, animated illustration, ${state}`}
          className={`absolute inset-0 h-full w-full ${animated ? "" : "invisible"}`} />}
      </div>
    </div>
    {controls && <div className="absolute inset-x-0 bottom-0 flex min-h-12 items-center justify-between gap-2 bg-[#eee7dc] px-4 text-xs text-[#514638]">
      <span role="status">{!member ? "Portrait preview" : status === "loading" ? "Preparing movement…" : status === "fallback" ? "Still portrait" : "Animated portrait"}</span>
      {member && (animated || !motion) && <button type="button" aria-pressed={!motion} onClick={() => setMotion(value => !value)}
        className="min-h-11 rounded px-2 font-medium hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#514638]">
        {motion ? "Pause movement" : "Resume movement"}
      </button>}
    </div>}
  </div>;
}
